import { initShaders } from "@/core/shaders/shaders"
import { createWebGLContext } from "@/core/backends/webgl/canvas"
import { Texture, TextureOptions } from "@/core/backends/webgl/texture"
import { waitClientSync } from "@/core/utils/fence-sync"
import { TextureFormat } from "@/core/backends/webgl/texture-format"
import { TextureArray, TextureArrayOptions } from "./webgl/texture-array"
import { Texture3D, Texture3DOptions } from "./webgl/texture-3d"
import { TextureMemoryCounters } from "@/utils/measurement/texture-memory-counters"

export class WebGLBackend {
	gl: WebGL2RenderingContext

	shaders: ReturnType<typeof initShaders>

	maxRenderSize: number

	unorm16supported = false

	textureFormat: TextureFormat = "rgba16float"

	textureMemoryCounters = new TextureMemoryCounters()

	constructor(gl?: WebGL2RenderingContext | null) {
		this.gl =
			gl ??
			createWebGLContext({
				depth: false,
				stencil: false,
				failIfMajorPerformanceCaveat: true,
				powerPreference: "high-performance",
			})

		this.gl.drawingBufferColorSpace = "display-p3"

		// JPEG images that are loaded will be converted to this color space
		// this.gl.unpackColorSpace = "display-p3"

		this.initExtensions()

		this.shaders = initShaders(this.gl)

		this.maxRenderSize = Math.min(
			this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE),
			this.gl.getParameter(this.gl.MAX_RENDERBUFFER_SIZE),
		)
	}

	get canvas() {
		return this.gl.canvas
	}

	initExtensions() {
		const halfFloatBufferExt = this.gl.getExtension("EXT_color_buffer_half_float")
		const floatBufferExt = this.gl.getExtension("EXT_color_buffer_float")
		const norm16TextureExt = this.gl.getExtension("EXT_texture_norm16")

		if (!halfFloatBufferExt && !floatBufferExt) {
			throw new Error("Cannot render to float textures")
		}

		if (halfFloatBufferExt) {
			this.textureFormat = "rgba16float"
		} else {
			this.textureFormat = "rgba32float"
		}

		if (norm16TextureExt) {
			this.unorm16supported = true

			// If we don't care about clipping blacks, then maybe this is a better format?
			// this.textureFormat = "rgba16unorm"
		}

		this.gl.getExtension("OES_texture_float")
		this.gl.getExtension("OES_texture_float_linear")
	}

	setCanvasSize(width: number, height: number) {
		this.gl.canvas.width = width * devicePixelRatio
		this.gl.canvas.height = height * devicePixelRatio
	}

	setViewport(x: number, y: number, width: number, height: number) {
		this.gl.viewport(x, y, width, height)
	}

	setScissor(x: number, y: number, width: number, height: number) {
		this.gl.scissor(x, y, width, height)
	}

	clear() {
		this.gl.clearColor(0, 0, 0, 0)
		this.gl.clear(this.gl.COLOR_BUFFER_BIT)
	}

	setFramebuffer(target: Texture | null, attachments?: Texture[]) {
		if (target) {
			target.bindFramebuffer(attachments)
		} else {
			this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null)
		}
	}

	enableScissor(enabled: boolean) {
		if (enabled) {
			this.gl.enable(this.gl.SCISSOR_TEST)
		} else {
			this.gl.disable(this.gl.SCISSOR_TEST)
		}
	}

	waitClientSync(callback: () => void) {
		waitClientSync(this.gl, callback)
	}

	createTexture(options?: TextureOptions) {
		return new Texture(this, options)
	}

	createTexture3D(options?: Texture3DOptions) {
		return new Texture3D(this, options)
	}

	createTextureArray(options: TextureArrayOptions) {
		return new TextureArray(this, options)
	}

	setFramebufferForTextureArray(target: TextureArray | null, layerIndex: number) {
		if (target) {
			target.bindFramebuffer(layerIndex)
		} else {
			this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null)
		}
	}

	setUniformBufferData(options: {
		buffer: WebGLBuffer
		data: Float32Array
		dataLength?: number
	}) {
		const { buffer, data, dataLength } = options

		this.gl.bindBuffer(this.gl.UNIFORM_BUFFER, buffer)
		this.gl.bufferSubData(
			this.gl.UNIFORM_BUFFER,
			0,
			dataLength != undefined ? data.slice(0, dataLength) : data,
		)
	}

	dispose() {
		// TODO: test
		// const ext = this.gl.getExtension("WEBGL_lose_context")
		// ext?.loseContext()
	}
}
